import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { firestore } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import './Inwil.css';

const weekdayNames = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];

const getWeekNumber = (date) => {
  const firstThursday = new Date(date.getFullYear(), 0, 4);
  const diff =
    (date - firstThursday) / 86400000 + firstThursday.getDay();
  return 1 + Math.floor(diff / 7);
};

const makeBlankSelected = () =>
  weekdayNames.reduce(
    (acc, d) => ({ ...acc, [d]: { morning: '', afternoon: '' } }),
    {}
  );

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const buildWeekOptions = () => {
  const options = [];
  for (let i = 0; i <= 4; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i * 7);
    const year = date.getFullYear();
    const week = getWeekNumber(date);
    const start = getWeekStart(date);
    const end = new Date(start);
    end.setDate(start.getDate() + 4);
    const label = `KW ${week} (${start.toLocaleDateString('de-CH')} - ${end.toLocaleDateString('de-CH')})`;
    options.push({ year, week, start, label });
  }
  return options;
};

const Reservation = () => {
  const db = firestore;
  const navigate = useNavigate();

  const weekOptions = useMemo(() => buildWeekOptions(), []);

  const [weekIdx, setWeekIdx] = useState(0);
  const [reservations, setReservations] = useState({});
  const [selectedStation, setSelectedStation] = useState(makeBlankSelected());
  const [showModal, setShowModal] = useState(false);

  const fetchWeekReservations = useCallback(
    async (loginName, year, week) => {
      const q = query(
        collection(db, 'reservations'),
        where('year', '==', year),
        where('week', '==', week)
      );

      const weekSnap = await getDocs(q);
      const userSel = makeBlankSelected();
      const allRes = {};
      const nameSnap = await getDocs(collection(db, 'users'));
      const userNames = {};
      nameSnap.forEach((d) => {
        const data = d.data();
        if (data.Name) userNames[d.id] = data.Name;
      });

      weekSnap.forEach((d) => {
        const { day, session, station, userName, userId } = d.data();

        if (userId === loginName) {
          userSel[day][session] = station;
        }

        if (!allRes[day]) allRes[day] = { morning: [], afternoon: [] };
        const display = userNames[userName] || userName;
        allRes[day][session].push({ station, userName: display });
      });

      return { userSel, allRes };
    },
    [db]
  );

  useEffect(() => {
    const loginName = localStorage.getItem('workerLoginName');
    if (!loginName) {
      navigate('/');
      return;
    }

    setSelectedStation(makeBlankSelected());
    setReservations({});

    let cancelled = false;
    const { year, week } = weekOptions[weekIdx];
    const load = async () => {
      const { userSel, allRes } = await fetchWeekReservations(loginName, year, week);
      if (!cancelled) {
        setSelectedStation(userSel);
        setReservations(allRes);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [navigate, weekIdx, fetchWeekReservations, weekOptions]);

  const handleReservation = async (day, session, station) => {

    const loginName = localStorage.getItem('workerLoginName');
    if (!loginName) {
      navigate('/');
      return;
    }

    const { year, week } = weekOptions[weekIdx];
    const prevStation = selectedStation[day][session];

    if (!station) {
      if (prevStation) {
        const oldDocId = `${year}-W${week}-${day}-${session}-S${prevStation}`;
        await deleteDoc(doc(db, 'reservations', oldDocId));
        setSelectedStation((prev) => ({
          ...prev,
          [day]: { ...prev[day], [session]: '' },
        }));
        setReservations((prev) => {
          const dayRes = prev[day] || {};
          const filtered = (dayRes[session] || []).filter(
            (r) => !(r.station === prevStation && r.userName === loginName)
          );
          return { ...prev, [day]: { ...dayRes, [session]: filtered } };
        });
      }
      return;
    }

    if (prevStation === station) return;

    const newDocId = `${year}-W${week}-${day}-${session}-S${station}`;
    const newRef = doc(db, 'reservations', newDocId);
    const snap = await getDoc(newRef);

    if (snap.exists()) {
      alert('Station bereits für diese Session reserviert.');
      return;
    }

    if (prevStation) {
      const oldDocId = `${year}-W${week}-${day}-${session}-S${prevStation}`;
      await deleteDoc(doc(db, 'reservations', oldDocId));
      setReservations((prev) => {
        const dayRes = prev[day] || {};
        const filtered = (dayRes[session] || []).filter(
          (r) => !(r.station === prevStation && r.userName === loginName)
        );
        return { ...prev, [day]: { ...dayRes, [session]: filtered } };
      });
    }

    await setDoc(newRef, {
      station,
      session,
      userName: loginName,
      userId: loginName,
      day,
      year,
      week,
    });

    setSelectedStation((prev) => ({
      ...prev,
      [day]: { ...prev[day], [session]: station },
    }));
    setReservations((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [session]: [...(prev[day]?.[session] || []), { station, userName: loginName }],
      },
    }));
  };

  const isDayLocked = (dayIdx) => {
    const today = new Date();
    const dayDate = new Date(weekOptions[weekIdx].start);
    dayDate.setDate(dayDate.getDate() + dayIdx);
    dayDate.setHours(0, 0, 0, 0);
    return dayDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const handleShowModal = async () => {
    const loginName = localStorage.getItem('workerLoginName');
    const { year, week } = weekOptions[weekIdx];
    const { userSel, allRes } = await fetchWeekReservations(loginName, year, week);
    setSelectedStation(userSel);
    setReservations(allRes);
    setShowModal(true);
  };
  const handleCloseModal = () => setShowModal(false);

  return (
    <div className="fade-in">
      <div className="reservation-container">
        <h1>Inwil</h1>

        <div className="week-select">
          <select value={weekIdx} onChange={(e) => setWeekIdx(Number(e.target.value))}>
            {weekOptions.map((w, i) => (
              <option key={i} value={i}>{w.label}</option>
            ))}
          </select>
        </div>

        <div className="week-container">
          {weekdayNames.map((day, idx) => (
            <div className="day-card" key={day}>
              <h3>{day}</h3>

              {['morning', 'afternoon'].map((session) => {
                const reserved = new Set(
                  reservations[day]?.[session]?.map((r) => r.station) || []
                );

                const ownChoice = selectedStation[day][session];
                const available = [...Array(10).keys()]
                  .map((n) => n + 1)
                  .filter((n) => !reserved.has(n) || n === ownChoice);

                return (
                  <div className={`session ${session}`} key={session}>
                    <h4>{session === 'morning' ? 'Morgen' : 'Nachmittag'}</h4>
                    <select
                      value={ownChoice}
                      disabled={isDayLocked(idx)}
                      onChange={(e) =>
                        handleReservation(day, session, Number(e.target.value))
                      }
                    >
                      <option value="">Wählen</option>
                      {available.map((n) => (
                        <option key={n} value={n}>
                          {`Station ${n}`}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <button className="reservation-list-button" onClick={handleShowModal}>
          Wer hat reserviert?
        </button>
      </div>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        dialogClassName="reservation-modal"
      >
        <Modal.Header>
          <Modal.Title>
            Reservierungen ({weekOptions[weekIdx].label})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Object.keys(reservations).length ? (
            <div className="reservation-table-container">
              <table className="reservation-table">
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th>Session</th>
                    <th>Station</th>
                    <th>Benutzer</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(reservations)
                    .sort(
                      ([dayA], [dayB]) =>
                        weekdayNames.indexOf(dayA) - weekdayNames.indexOf(dayB)
                    )
                    .map(([d, sess]) => (
                      <React.Fragment key={d}>
                        {sess.morning?.map((r, i) => (
                          <tr key={`${d}-m-${i}`}>
                            <td>{d}</td>
                            <td>Morgen</td>
                            <td>Station {r.station}</td>
                            <td>{r.userName}</td>
                          </tr>
                        ))}
                        {sess.afternoon?.map((r, i) => (
                          <tr key={`${d}-a-${i}`}>
                            <td>{d}</td>
                            <td>Nachmittag</td>
                            <td>Station {r.station}</td>
                            <td>{r.userName}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>Keine Reservierungen für {weekOptions[weekIdx].label}.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Reservation;
